import React from 'react';
import { splitAnswerSegments, splitContacts, trimPartialLink } from '../../lib/streamParse.mjs';

/**
 * Shared answer-rendering pieces for the chat surfaces (inline card, floating
 * panel, admin preview). Extracted from ChatWidget in W2 so every surface
 * renders citations/sources identically. All chip hrefs come ONLY from the
 * sources SSE event — never from model-authored text.
 */

/** Group chunk-level sources by underlying document (URL minus page/timestamp),
 *  preserving first-appearance order. */
export function groupSources(sources) {
  const groups = new Map();
  for (const s of sources) {
    const base = (s.url || '').split('#')[0].replace(/[?&]t=\d+$/, '');
    const key = `${s.source_type}|${base}|${s.title || ''}`;
    if (!groups.has(key)) {
      const label = s.source_type === 'pdf' ? `PDF: ${s.title || base}`
        : s.source_type === 'transcript' ? `Video: ${s.title || base}`
        : (s.title || base);
      groups.set(key, { key, label, date: s.publish_date || '', ns: [], items: [] });
    }
    const g = groups.get(key);
    g.ns.push(s.n);
    g.items.push(s);
  }
  return [...groups.values()];
}

/** Plain answer text with emails and phone numbers as mailto:/tel: links. */
function renderText(value, key) {
  const parts = splitContacts(value);
  if (parts.length === 1 && parts[0].type === 'text') return <span key={key}>{value}</span>;
  return (
    <span key={key}>
      {parts.map((p, i) => {
        if (p.type === 'email') return <a key={i} href={`mailto:${p.value}`} className="inquiros-inline-link">{p.value}</a>;
        if (p.type === 'phone') return <a key={i} href={`tel:${p.tel}`} className="inquiros-inline-link">{p.value}</a>;
        return p.value;
      })}
    </span>
  );
}

/**
 * Render answer text: citation chips for "[n]", named links for "[Name][n]",
 * tappable emails and phone numbers. Every href comes from the sources event
 * (or is mailto:/tel:). `streaming` hides a named link still arriving.
 */
export function renderAnswer(text, sourceByN, onCite, { streaming = false } = {}) {
  if (!text) return null;
  return splitAnswerSegments(streaming ? trimPartialLink(text) : text).map((seg, key) => {
    if (seg.type === 'text') return renderText(seg.value, key);
    const src = sourceByN[seg.n];
    if (seg.type === 'link') {
      // unknown source: keep the name as text rather than a dead link
      if (!src) return renderText(seg.text, key);
      return (
        <a
          key={key} href={src.url} target="_blank" rel="noopener noreferrer"
          className="inquiros-inline-link" onClick={onCite ? () => onCite(src) : undefined}
          title={src.display || src.title || src.url}
        >{seg.text}</a>
      );
    }
    // no matching source -> render nothing (never show an unresolved chip)
    if (!src) return null;
    return (
      <a
        key={key} href={src.url} target="_blank" rel="noopener noreferrer"
        className="inquiros-chip" onClick={onCite ? () => onCite(src) : undefined}
        aria-label={`Source ${seg.n}: ${src.display || src.title || src.url}`}
      >[{seg.n}]</a>
    );
  });
}

/** How many document groups the expanded list shows before "Show N more". */
export const SOURCES_PREVIEW = 5;

/**
 * Sources under one answer. Collapsed to a "Sources (N)" toggle by default
 * (the tenant's widget.sources_display flips it to open) so a dozen retrieved
 * documents never crowd out the answer. Inside: the documents the answer
 * actually cites first, in citation order, then the ones the model was shown
 * but did not cite under an "Also consulted" heading — that distinction was
 * previously conveyed only by fading the row to half opacity, which read as
 * "broken link". Uncited chips are outlined, cited chips solid; all open the
 * source, and so does each document's title. The first SOURCES_PREVIEW groups
 * show; the rest sit behind "Show N more".
 *
 * citedOnly (sources_display "cited") lists just the documents the answer
 * used. A site's reference links (portal login, plan provisions) were offered
 * to the model, not consulted, so they are listed only when cited.
 */
export function SourcesBlock({ sources, used, onCite, mutedColor, textColor, defaultOpen = false,
                               citedOnly = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [showAll, setShowAll] = React.useState(false);
  const listId = React.useId();
  if (!sources || !sources.length) return null;

  const usedList = used || [];
  const groups = groupSources(sources.filter((s) => s.source_type !== 'link' || usedList.includes(s.n)));
  const firstCite = (g) => Math.min(...g.ns.map((n) => {
    const i = usedList.indexOf(n);
    return i < 0 ? Infinity : i;
  }));
  const cited = groups.filter((g) => firstCite(g) !== Infinity).sort((a, b) => firstCite(a) - firstCite(b));
  const consulted = citedOnly ? [] : groups.filter((g) => firstCite(g) === Infinity);
  const ordered = [...cited, ...consulted];
  if (!ordered.length) return null;
  const visible = showAll ? ordered : ordered.slice(0, SOURCES_PREVIEW);
  const hidden = ordered.length - visible.length;
  const firstConsulted = visible.find((g) => consulted.includes(g));

  const chip = (s) => {
    const isCited = usedList.includes(s.n);
    return (
      <a key={s.n} href={s.url} target="_blank" rel="noopener noreferrer"
         className={`inquiros-chip${isCited ? '' : ' inquiros-chip--unused'}`}
         onClick={onCite ? () => onCite(s) : undefined}
         title={s.display || s.title || s.url}
         aria-label={`Source ${s.n}: ${s.display || s.title || s.url}`}
      >[{s.n}]</a>
    );
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button" className="inquiros-sources-toggle"
        aria-expanded={open} aria-controls={listId}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="inquiros-sources-caret" aria-hidden="true">{open ? '\u25BE' : '\u25B8'}</span>
        Sources ({ordered.length})
        {!open && cited.length ? (
          <span className="inquiros-sources-hint">{cited.length} cited</span>
        ) : null}
      </button>
      {open ? (
        <ol id={listId} style={{ margin: '6px 0 0', paddingLeft: 0, listStyle: 'none' }}>
          {visible.map((g) => {
            // the title opens the part of the document the answer cited
            const lead = g.items.find((s) => usedList.includes(s.n)) || g.items[0];
            return (
              <React.Fragment key={g.key}>
                {g === firstConsulted ? (
                  <li aria-hidden="true" style={{ fontSize: 11.5, fontWeight: 600, color: mutedColor, margin: '6px 0 3px' }}>
                    Also consulted
                  </li>
                ) : null}
                <li style={{ marginBottom: 5, fontSize: 13 }}>
                  <a href={lead.url} target="_blank" rel="noopener noreferrer" className="inquiros-source-title"
                     onClick={onCite ? () => onCite(lead) : undefined}
                     style={{ color: consulted.includes(g) ? mutedColor : textColor }}>{g.label}</a>
                  {g.date ? <span style={{ color: mutedColor }}> ({g.date})</span> : null}{' '}
                  {g.items.map(chip)}
                </li>
              </React.Fragment>
            );
          })}
          {hidden > 0 ? (
            <li>
              <button type="button" className="inquiros-sources-more" onClick={() => setShowAll(true)}>
                Show {hidden} more
              </button>
            </li>
          ) : null}
        </ol>
      ) : null}
    </div>
  );
}

export function palette(isDark) {
  return isDark
    ? { bg: '#1a1a1a', text: '#e8e8e8', border: '#333', inputBg: '#242424', muted: '#9a9a9a',
        accent: '#4f9cf9', error: '#f87171', botBubble: '#242424', userText: '#ffffff' }
    : { bg: '#ffffff', text: '#1a1a1a', border: '#e2e2e2', inputBg: '#fff', muted: '#6b7280',
        accent: '#1e5eb8', error: '#dc2626', botBubble: '#f3f4f6', userText: '#ffffff' };
}

export function CHIP_CSS(c) {
  return `
  .inquiros-chat .sr-only { position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0; }
  .inquiros-chat .inquiros-chip {
    display:inline-block; min-width:20px; text-align:center; padding:0 5px; margin:0 1px;
    font-size:12px; font-weight:600; line-height:1.4; border-radius:4px; text-decoration:none;
    color:#fff; background:${c.accent}; cursor:pointer; vertical-align:baseline;
  }
  .inquiros-chat .inquiros-chip--unused { color:${c.accent}; background:transparent; box-shadow:inset 0 0 0 1px ${c.accent}; }
  .inquiros-chat .inquiros-inline-link { color:${c.accent}; font-weight:600; text-decoration:underline; text-underline-offset:2px; }
  .inquiros-chat .inquiros-source-title { text-decoration:underline; text-underline-offset:2px; text-decoration-thickness:1px; }
  .inquiros-chat .inquiros-source-title:hover, .inquiros-chat .inquiros-inline-link:hover { text-decoration-thickness:2px; }
  .inquiros-chat .inquiros-sources-toggle {
    display:inline-flex; align-items:center; gap:5px; background:none; border:none; padding:0;
    font:inherit; font-size:12px; font-weight:600; color:${c.muted}; cursor:pointer;
  }
  .inquiros-chat .inquiros-sources-toggle:hover { color:${c.text}; }
  .inquiros-chat .inquiros-sources-caret { font-size:10px; width:10px; display:inline-block; }
  .inquiros-chat .inquiros-sources-hint { font-weight:400; }
  .inquiros-chat .inquiros-sources-hint::before { content:"\\00b7"; margin:0 5px; }
  .inquiros-chat .inquiros-sources-more {
    background:none; border:none; padding:0; font:inherit; font-size:12px; color:${c.accent};
    cursor:pointer; text-decoration:underline;
  }
  /* Host pages (WordPress themes, resets) often force placeholders to full
     text color, some with !important; the prompt hint must read as a hint on
     every site, so this wins on both specificity (the id) and priority. The
     opacity mirrors browsers' own placeholder rendering — lighter than the
     muted text tone. One unprefixed rule on purpose: a vendor-prefixed
     pseudo-element in the same selector list would make browsers that don't
     know it drop the whole rule. */
  .inquiros-chat input::placeholder, .inquiros-chat #inquiros-q::placeholder {
    color:${c.muted} !important; opacity:.6 !important;
  }
  .inquiros-chat .inquiros-chip:focus-visible,
  .inquiros-chat .inquiros-vote:focus-visible,
  .inquiros-chat .inquiros-iconbtn:focus-visible,
  .inquiros-chat .inquiros-starter:focus-visible,
  .inquiros-chat a:focus-visible,
  .inquiros-chat input:focus-visible,
  .inquiros-chat button:focus-visible { outline:3px solid ${c.accent}; outline-offset:2px; }
  .inquiros-chat .inquiros-vote { background:none;border:1px solid ${c.border};border-radius:6px;padding:3px 7px;cursor:pointer;font-size:14px; }
  .inquiros-chat .inquiros-iconbtn { background:none;border:1px solid ${c.border};border-radius:6px;padding:3px 8px;cursor:pointer;font-size:12px;color:${c.muted}; }
  .inquiros-chat .inquiros-starter {
    display:inline-block; margin:0 6px 6px 0; padding:6px 12px; font-size:13px; cursor:pointer;
    border:1px solid ${c.border}; border-radius:999px; background:${c.inputBg}; color:${c.text};
  }
  .inquiros-chat .inquiros-starter:hover { border-color:${c.accent}; color:${c.accent}; }
  .inquiros-chat .inquiros-caret { animation:inquiros-blink 1s steps(2) infinite; margin-left:2px; color:${c.muted}; }
  @keyframes inquiros-blink { 0%,50%{opacity:1;} 51%,100%{opacity:0;} }
  @media (prefers-reduced-motion: reduce) { .inquiros-chat .inquiros-caret { animation:none; } }
  `;
}
